import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { api } from '../../lib/api.ts'
import { renderMarkdown } from '../../lib/markdown.ts'
import { MODIFIER } from '../../lib/platform.ts'
import { relativeTime } from '../../lib/relativeTime.ts'
import type { User } from '../../lib/types.ts'
import { useRemoteChange } from '../../hooks/useRealtime.tsx'
import { useSession } from '../../hooks/useSession.ts'
import { useUsers } from '../../hooks/useTasks.ts'
import { EnterHighlight } from '../../ui/EnterHighlight.tsx'
import { MentionField } from '../../ui/MentionField.tsx'
import { EmptyState, ErrorState, Kbd } from '../../ui/primitives.tsx'
import { Avatar, userColorVar } from '../shell/Avatar.tsx'
import styles from './comments.module.css'

export interface Comment {
  id: number
  taskId: number
  authorId: number
  contentMd: string
  createdAt: string
  editedAt: string | null
}

const commentsApi = {
  list: (taskId: number) => api<{ comments: Comment[] }>(`/api/tasks/${taskId}/comments`),
  create: (taskId: number, contentMd: string) =>
    api<{ comment: Comment }>(`/api/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ contentMd }),
    }),
  update: (id: number, contentMd: string) =>
    api<{ comment: Comment }>(`/api/comments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ contentMd }),
    }),
  remove: (id: number) => api<void>(`/api/comments/${id}`, { method: 'DELETE' }),
}

export function useCommentMutations(taskId: number) {
  const queryClient = useQueryClient()
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['comments', taskId] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['task'] })
  }
  return {
    create: useMutation({
      mutationFn: (contentMd: string) => commentsApi.create(taskId, contentMd),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, contentMd }: { id: number; contentMd: string }) =>
        commentsApi.update(id, contentMd),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: number) => commentsApi.remove(id),
      onSuccess: invalidate,
    }),
  }
}

/**
 * Flat thread (PRD §3). Do not grow nested replies here.
 *
 * The composer is NOT part of this — it is pinned below the drawer's scroll area (ui-spec §5.2),
 * so it lives as a sibling in TaskDrawer. Rendering it inside would put new comments underneath it.
 */
export function CommentThread({ taskId }: { taskId: number }): React.JSX.Element {
  const users = useUsers()
  const session = useSession()
  const { update, remove } = useCommentMutations(taskId)

  const comments = useQuery({
    queryKey: ['comments', taskId],
    queryFn: async () => (await commentsApi.list(taskId)).comments,
  })

  const list = comments.data ?? []

  return (
    <div className={styles.thread}>
      {comments.isError && (
        <ErrorState
          text="Nie udało się wczytać komentarzy."
          onRetry={() => void comments.refetch()}
        />
      )}

      {list.length === 0 && !comments.isPending && !comments.isError && (
        <EmptyState
          text="Nie ma jeszcze komentarzy"
          variant="plain"
          action={{
            label: 'Rozpocznij wątek',
            onClick: () =>
              document.querySelector<HTMLTextAreaElement>(`.${styles.composerArea}`)?.focus(),
          }}
        />
      )}

      {list.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          author={users.data?.find((u) => u.id === comment.authorId) ?? null}
          isMine={comment.authorId === session.data?.id}
          onSave={(contentMd) => update.mutate({ id: comment.id, contentMd })}
          onDelete={() => remove.mutate(comment.id)}
        />
      ))}
    </div>
  )
}

function CommentItem({
  comment,
  author,
  isMine,
  onSave,
  onDelete,
}: {
  comment: Comment
  author: User | null
  isMine: boolean
  onSave: (contentMd: string) => void
  onDelete: () => void
}): React.JSX.Element {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.contentMd)
  const flash = useRemoteChange('task_comments', comment.id)

  useEffect(() => {
    setDraft(comment.contentMd)
  }, [comment.contentMd])

  return (
    <article className={styles.item}>
      <EnterHighlight active={flash} />
      <Avatar
        name={author?.displayName ?? 'agent'}
        colorVar={author ? userColorVar(author.id) : '--warn'}
        size={24}
      />

      <div>
        <div className={styles.head}>
          <span className={`${styles.author}${author ? '' : ` ${styles.authorAgent}`}`}>
            {author?.displayName ?? 'agent'}
          </span>
          <span className={styles.time}>{relativeTime(comment.createdAt)}</span>
          {comment.editedAt && <span className={styles.edited}>(edytowany)</span>}

          {isMine && !editing && (
            <span className={styles.menu}>
              <button type="button" className={styles.menuButton} onClick={() => setEditing(true)}>
                edytuj
              </button>
              <button
                type="button"
                className={`${styles.menuButton} ${styles.menuDanger}`}
                onClick={onDelete}
              >
                usuń
              </button>
            </span>
          )}
        </div>

        {editing ? (
          <>
            <textarea
              className={styles.editArea}
              value={draft}
              // Focus po jawnym kliknięciu „Edytuj" — użytkownik właśnie o to poprosił.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Edytuj komentarz"
            />
            <div className={styles.editActions}>
              <button
                type="button"
                className={styles.send}
                disabled={!draft.trim()}
                onClick={() => {
                  onSave(draft.trim())
                  setEditing(false)
                }}
              >
                Zapisz
              </button>
              <button
                type="button"
                className={styles.cancel}
                onClick={() => {
                  setDraft(comment.contentMd)
                  setEditing(false)
                }}
              >
                Anuluj
              </button>
            </div>
          </>
        ) : (
          <div
            className={`markdown ${styles.body}`}
            // Safe by construction: markdown-it runs with html:false, so raw HTML in the source is
            // escaped rather than parsed, and link schemes are allow-listed (lib/markdown.ts).
            dangerouslySetInnerHTML={{ __html: renderMarkdown(comment.contentMd) }}
          />
        )}
      </div>
    </article>
  )
}

/** Pinned below the drawer's scroll area, not inside the thread (ui-spec §5.2). */
export function CommentComposer({ taskId }: { taskId: number }): React.JSX.Element {
  const { create } = useCommentMutations(taskId)
  const onSend = (contentMd: string): void => create.mutate(contentMd)
  const sending = create.isPending
  const [value, setValue] = useState('')
  const area = useRef<HTMLTextAreaElement>(null)

  const grow = (el: HTMLTextAreaElement): void => {
    el.style.height = 'auto'
    el.style.height = `${Math.max(56, el.scrollHeight)}px`
  }

  const send = (): void => {
    const content = value.trim()
    if (!content || sending) return
    onSend(content)
    setValue('')
    if (area.current) {
      area.current.style.height = '56px'
      area.current.focus()
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      send()
    }
  }

  return (
    <div className={styles.composer}>
      <MentionField
        textareaRef={area}
        className={styles.composerArea}
        value={value}
        placeholder="Napisz komentarz…"
        aria-label="Nowy komentarz"
        onChange={(next) => {
          setValue(next)
          if (area.current) grow(area.current)
        }}
        onKeyDown={onKeyDown}
      />
      <div className={styles.composerFoot}>
        <span className={styles.hint}>
          <Kbd>{MODIFIER}</Kbd>
          <Kbd>↵</Kbd>
          wysyła
        </span>
        <button
          type="button"
          className={styles.send}
          disabled={!value.trim() || sending}
          onClick={send}
        >
          {sending ? 'Wysyłanie…' : 'Wyślij'}
        </button>
      </div>
    </div>
  )
}
